import React from 'react';
import { Modal, ModalContent, ModalBody, Button } from '@heroui/react';
import { ResponsiveModal, Typography } from '../../../../components';
import AlertBlock from '../../../../components/alert-block';
import {
  BronzeLvl,
  SilverLvl,
  GoldLvl,
  PlatinumLvl,
  DiamondLvl,
} from '../../../../components/icons';

interface LevelInfo {
  name: string;
  commission: string;
  minAmount: string;
  icon: React.ReactNode;
}

interface PartnerLevelModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLevel: string;
  currentLevelKey: string;
  currentCommission: string;
  nextLevel: string;
  nextCommission: string;
  amountToNextLevel: string;
  levels: any;
  t: (key: string, params?: Record<string, any>) => string;
}

const PartnerLevelModal: React.FC<PartnerLevelModalProps> = ({
  isOpen,
  onClose,
  currentLevel,
  currentLevelKey,
  currentCommission,
  nextLevel,
  nextCommission,
  amountToNextLevel,
  levels: levelsData,
  t,
}) => {
  const levelOrder = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];

  const getLevelIcon = (levelKey: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      bronze: <BronzeLvl />,
      silver: <SilverLvl />,
      gold: <GoldLvl />,
      platinum: <PlatinumLvl />,
      diamond: <DiamondLvl />,
    };
    return iconMap[levelKey] || <BronzeLvl />;
  };

  const levels: LevelInfo[] = levelOrder.map((levelKey) => {
    const levelInfo = levelsData[levelKey];
    return {
      name: t(`${levelKey}`),
      commission: `${levelInfo.commition}%`,
      minAmount: `$${levelInfo.cost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`,
      icon: getLevelIcon(levelKey),
    };
  });

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[35.375rem]',
        body: 'p-0',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalBody>
              {/* Header */}
              <div className="border-b border-auth-surface-hover px-4 py-5 flex flex-col ">
                <div className="flex gap-2 items-center">
                  <Typography variant="label1" className="text-auth-ink">
                    {t('proposals.partnerLevelModal.title')}
                  </Typography>
                  <div className="flex gap-0.5 items-center">
                    {getLevelIcon(currentLevelKey)}
                    <Typography variant="label1" className=" text-auth-ink">
                      {currentLevel} ({currentCommission})
                    </Typography>
                  </div>
                </div>
                <Typography variant="body3" className="pt-2 text-auth-muted">
                  {t('proposals.partnerLevelModal.description')}
                </Typography>
              </div>

              {/* Levels List */}
              <div className="border-b border-auth-surface-hover px-4 py-4 flex flex-col gap-2">
                {levels.map((level, index) => (
                  <div key={index} className="flex gap-1 items-center">
                    <div className="flex gap-0.5 items-center">
                      {level.icon}
                      <Typography variant="label2" className="!capitalize text-auth-ink">
                        {level.name} ({level.commission})
                      </Typography>
                    </div>
                    <Typography variant="body2" className="text-auth-muted">
                      {t('proposals.partnerLevelModal.from')} {level.minAmount}
                    </Typography>
                  </div>
                ))}
              </div>

              {/* Info Block */}
              <div className="border-b border-[#E2EBF7] px-4 py-4 flex flex-col gap-4 items-end">
                <div className="w-full">
                  <AlertBlock
                    data={[
                      {
                        severity: 'info',
                        title: t('proposals.partnerLevelModal.achievedLevel', {
                          level: currentLevel,
                        }),
                        description: `${t('proposals.partnerLevelModal.nextMonthCommission', { commission: currentCommission })} ${t(
                          'proposals.partnerLevelModal.toAchieveLevel',
                          {
                            level: `<strong>${nextLevel}</strong>`,
                            commission: nextCommission,
                            amount: `<strong>${amountToNextLevel}</strong>`,
                          }
                        )}`,
                      },
                    ]}
                  />
                </div>
                <Button
                  className="bg-primary text-white h-8 px-3 text-xs rounded-lg"
                  onPress={onClose}
                >
                  {t('proposals.partnerLevelModal.close')}
                </Button>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default PartnerLevelModal;
